import React from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Tabs, Tab } from "@mui/material";
import { useClinicTheme } from "../../theme";
import { COLORS } from "../../styles/uiConstants";
import PageHeaderBar from "../molecules/PageHeaderBar";

/**
 * Sekmeli sayfa kabuğu — Personel Yönetimi (`/Isletme-Performansi`) sayfasının
 * deseni: ince beyaz başlık şeridi (avatar + başlık + açıklama), altında
 * kaydırılabilir sekme satırı, altında içerik.
 *
 * Aktif sekme URL'de `?tab=` ile taşınır; sayfa yenilense de korunur. Tek
 * sekmeli sayfalarda sekme satırı yine çizilir — sekmeler sonradan
 * eklendiğinde yerleşim değişmesin diye.
 *
 * @param {React.ReactNode} icon      Başlık avatarının içindeki ikon
 * @param {string} title              Sayfa başlığı
 * @param {string} [subtitle]         Başlık altı açıklama
 * @param {React.ReactNode} [action]  Başlık şeridinin sağındaki aksiyon
 * @param {Array<{key: string, label: string, icon?: React.ReactNode}>} tabs
 * @param {(tabKey: string) => React.ReactNode} children  Aktif sekmenin içeriği
 */
export default function TabbedPageShell({ icon, title, subtitle, action, tabs = [], children }) {
  const { palette } = useClinicTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultTab = tabs[0]?.key;
  const requested = searchParams.get("tab");
  const activeTab = tabs.some((t) => t.key === requested) ? requested : defaultTab;
  const tabIndex = Math.max(0, tabs.findIndex((t) => t.key === activeTab));

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: COLORS.slate50,
      }}
    >
      <Box sx={{ flexShrink: 0, bgcolor: COLORS.white, borderBottom: `1px solid ${COLORS.slate200}` }}>
        <PageHeaderBar icon={icon} title={title} subtitle={subtitle} action={action} divider={false} />

        <Tabs
          value={tabIndex}
          onChange={(_, newVal) => setSearchParams({ tab: tabs[newVal].key }, { replace: true })}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: { xs: 2, md: 4 },
            minHeight: 42,
            "& .MuiTab-root": {
              color: COLORS.slate400,
              fontWeight: 600,
              fontSize: "0.8125rem",
              textTransform: "none",
              minHeight: 42,
              py: 1,
              px: 1.5,
              minWidth: "auto",
              "&.Mui-selected": { color: palette.primary },
              "&:hover": { color: COLORS.slate700 },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: palette.primary,
              height: 2,
              borderRadius: "2px 2px 0 0",
            },
            "& .MuiTabs-scrollButtons": { color: COLORS.slate400, width: 28 },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {tab.icon ? (
                    <span style={{ lineHeight: 1, display: "flex", alignItems: "center" }}>
                      {tab.icon}
                    </span>
                  ) : null}
                  {tab.label}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: "hidden", minHeight: 0, display: "flex", flexDirection: "column" }}>
        {typeof children === "function" ? children(activeTab) : children}
      </Box>
    </Box>
  );
}
